import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { catchError, map, of, tap, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private initialized = false;
  private currentUser: CurrentUser | null = null;
  private apiUrl = `${environment.backendUrl}/api`;

  private readonly oauth = inject(OAuthService);
  private readonly injector = inject(Injector);
  private readonly http = inject(HttpClient);

  async init(): Promise<void> {
    try {
      const windowPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const windowSearch = typeof window !== 'undefined' ? window.location.search : '';
      const router = this.injector.get(Router);
      const url = router?.url ?? `${windowPath}${windowSearch}`;
      const isRegistrationRoute = windowPath.startsWith('/registration') || url.startsWith('/registration');
      const isUserCamperRoute = windowPath.startsWith('/c/') || url.startsWith('/c/');
      if (isRegistrationRoute || isUserCamperRoute) {
        this.initialized = true;
        return;
      }
    } catch (e) {
      // ignore if Router isn't available yet
    }

    if (this.initialized) {
      if (!this.isAuthenticated()) {
        await this.oauth.loadDiscoveryDocumentAndTryLogin();
        if (!this.isAuthenticated()) {
          if (this.hasStoredAccessToken()) {
            this.logout();
          } else {
            this.login();
          }
          return new Promise(() => undefined);
        }
      }
      return;
    }

    const cfg: AuthConfig = {
      issuer: environment.oidc.issuer,
      clientId: environment.oidc.clientId,
      responseType: 'code',
      redirectUri: environment.oidc.redirectUri,
      postLogoutRedirectUri: environment.oidc.postLogoutRedirectUri,
      scope: environment.oidc.scope,
      requireHttps: environment.oidc.requireHttps,
      showDebugInformation: !environment.production,
      disablePKCE: false,
      sessionChecksEnabled: false,
      skipIssuerCheck: !environment.oidc.requireHttps,
      strictDiscoveryDocumentValidation: false,
    };

    this.oauth.configure(cfg);
    this.oauth.setStorage(sessionStorage);
    this.oauth.setupAutomaticSilentRefresh();

    await this.oauth.loadDiscoveryDocumentAndTryLogin();
    if (!this.isAuthenticated()) {
      const refreshToken = this.oauth.getRefreshToken();
      if (refreshToken) {
        try {
          await this.oauth.refreshToken();
        } catch (err) {
          console.info('[auth] refresh token failed', err);
        }
      }
    }
    this.initialized = true;
    if (!this.isAuthenticated()) {
      console.info(this.hasValidAccessToken());

      if (this.hasStoredAccessToken()) {
        this.logout();
      } else {
        this.login();
      }
      return new Promise(() => undefined);
    }
  }

  login(): void {
    this.oauth.initCodeFlow();
  }

  logout(): void {
    this.oauth.logOut();
    const router = this.injector.get(Router);
    router.navigateByUrl('/home');
  }

  isAuthenticated(): boolean {
    return this.hasValidAccessToken();
  }

  getAccessToken(): string | null {
    const token = this.oauth.getAccessToken();
    return token || null;
  }

  private hasValidAccessToken(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }
    const expMs = this.getTokenExpiryMs(token);
    if (expMs !== null) {
      return Date.now() < expMs;
    }
    return this.oauth.hasValidAccessToken();
  }

  private hasStoredAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  private getTokenExpiryMs(token: string): number | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(parts[1])) as Record<string, unknown>;
      if (typeof payload['exp'] !== 'number') {
        return null;
      }
      return payload['exp'] * 1000;
    } catch {
      return null;
    }
  }

  getRoles(): string[] {
    const token = this.getAccessToken();
    if (!token) {
      return [];
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1] || '')) as Record<string, unknown>;
      const roles = new Set<string>();

      const addRole = (value: unknown) => {
        if (!value) return;
        let role = value.toString().trim();
        if (!role) return;
        if (role.startsWith('ROLE_')) {
          role = role.substring(5);
        }
        roles.add(role);
      };

      const rawRoles = (payload['roles'] ?? payload['role']) as unknown;
      if (Array.isArray(rawRoles)) {
        rawRoles.forEach(addRole);
      } else if (typeof rawRoles === 'string') {
        rawRoles.split(/\s+/).forEach(addRole);
      }

      return Array.from(roles);
    } catch (err) {
      console.error('Failed to parse JWT for roles', err);
      return [];
    }
  }

  getGroups(): string[] {
    return this.getRoles();
  }

  hasAnyGroup(...expected: string[]): boolean {
    const roles = this.getRoles();
    return roles.some((r) => expected.includes(r));
  }

  // Backward compatibility
  hasAnyRole(...expected: string[]): boolean {
    const roles = this.getRoles();
    return roles.some((r) => expected.includes(r));
  }

  loadCurrentUser(): Observable<CurrentUser | null> {
    if (!this.isAuthenticated()) {
      this.currentUser = null;
      return of(null);
    }

    return this.http.get<CurrentUser>(`${this.apiUrl}/users/me`).pipe(
      tap((user) => (this.currentUser = user ?? null)),
      map((user) => user ?? null),
      catchError((err) => {
        console.error('Failed to load current user', err);
        this.currentUser = null;
        return of(null);
      }),
    );
  }

  loadCamperByUserLink(userlink: string): Observable<CamperPublicMe | null> {
    const normalized = (userlink ?? '').trim();
    if (!normalized) {
      return of(null);
    }
    return this.http.get<CamperPublicMe>(`${environment.backendUrl}/api/user-camper/me?userlink=${encodeURIComponent(normalized)}`).pipe(
      map((camper) => {
        if (!camper) {
          return null;
        }
        const hasUsefulData = Boolean(camper.id || camper.userLink || camper.startDate || camper.endDate);
        return hasUsefulData ? camper : null;
      }),
      catchError((err) => {
        if (err?.status !== 404) {
          console.error('Failed to load camper by userlink', err);
        }
        return of(null);
      }),
    );
  }

  loadCamperTimetableByUserLink(userlink: string): Observable<CamperPublicTimetableOccurrence[]> {
    const normalized = (userlink ?? '').trim();
    if (!normalized) {
      return of([]);
    }
    return this.http
      .get<
        CamperPublicTimetableOccurrence[]
      >(`${environment.backendUrl}/api/user-camper/timetable?userlink=${encodeURIComponent(normalized)}`)
      .pipe(
        catchError((err) => {
          if (err?.status !== 404) {
            console.error('Failed to load camper timetable by userlink', err);
          }
          return of([]);
        }),
      );
  }

  getCurrentUser(): CurrentUser | null {
    return this.currentUser;
  }

  isSUperAdmin(): boolean {
    return this.hasAnyRole('ILASY_SU_ADMIN');
  }

  resolvePostLoginRedirect(): string {
    if (this.hasAnyRole('ILASY_ADMIN', 'ILASY_SU_ADMIN')) {
      return '/admin';
    }
    if (this.hasAnyRole('ILASY_USER')) {
      return '/c';
    }
    return '/';
  }

  async ensureAuthenticated(): Promise<boolean> {
    if (this.isAuthenticated()) {
      return true;
    }
    this.login();
    return false;
  }

  async redirectIfAuthenticated(targetUrl = '/'): Promise<void> {
    if (this.isAuthenticated()) {
      const router = this.injector.get(Router);
      await router.navigateByUrl(targetUrl);
    }
  }

  async loadCurrentUserOnce(): Promise<CurrentUser | null> {
    return firstValueFrom(this.loadCurrentUser());
  }
}

export interface CurrentUser {
  id: number;
  firstName?: string;
  lastName?: string;
  username: string;
  email?: string;
  role?: string;
  enabled?: boolean;
  language?: string;
  lastCampEventId?: string;
  accessibleCampEvents?: Array<{ uuid: string; name?: string }>;
  accessZones?: string[];
  accessLevelScopes?: string[][];
  accessLocalities?: string[];
}

export interface CamperPublicMe {
  id?: number;
  userLink?: string;
  startDate?: string;
  endDate?: string;
}

export interface CamperPublicTimetableOccurrence {
  id?: string;
  title?: string;
  start?: string | Date | null;
  end?: string | Date | null;
  color?: unknown;
  groupId?: string;
  type?: string;
  taskGroupIds?: number[];
  studyGroupIds?: number[];
}
